import { Component, EventEmitter, Input, Output, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import * as bootstrap from 'bootstrap';
import { Setor } from 'src/app/shared/interfaces/setor/setor.interface';
import { ProdutoSetor } from 'src/app/shared/interfaces/produto/produto-setor.interface';
import { SetorAPIService } from 'src/app/modules/setor/services/setor-api.service';
import { TarefaPedidoAPIService } from 'src/app/modules/tarefa-pedido/services/tarefa-pedido-api.service';
import { ProdutoAPIService } from 'src/app/modules/produto/services/produto-api.service';
import { ToastrService } from 'ngx-toastr';
import { forkJoin } from 'rxjs';
import { Produto } from 'src/app/shared/interfaces/produto/produto.interface';
import { PedidoItem } from 'src/app/shared/interfaces/pedido/pedido-item.interface';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';

interface SetorComOrdem {
  id: number | null;
  ordem: number;
  setor: Setor | null; // null quando está em aberto para preenchimento
  isEmAberto: boolean; // indica se está aguardando preenchimento
  produtoId: number;
  setorId: number | null;
}

@Component({
  selector: 'app-modal-setores-producao',
  templateUrl: './modal-setores-producao.component.html',
  styleUrl: './modal-setores-producao.component.scss'
})
export class ModalSetoresProducaoComponent implements AfterViewInit {
  @Output() setoresSalvos = new EventEmitter<ProdutoSetor[]>();
  @Output() modalFechada = new EventEmitter<void>();
  @Output() setorAdicionadoLocal = new EventEmitter<ProdutoSetor>(); // Novo evento para produto.id == 0
  @Output() setorRemovidoLocal = new EventEmitter<ProdutoSetor>();
  @Output() ordenacaoAlteradaLocal = new EventEmitter<ProdutoSetor[]>(); // Evento para quando a ordem muda via drag and drop

  public setores: Setor[] = [];
  public setoresSelecionados: ProdutoSetor[] = [];
  public setoresOrdenados: SetorComOrdem[] = [];
  public produto: Produto = undefined;
  public nomeProduto: string = '';
  public isLoading: boolean = false;
  public isTableLoading: boolean = false; // Loading específico para a tabela
  public loadingMessage: string = ''; // Mensagem do loading
  private modalInstance: any;
  private produtoId: number = 0;
  private pedidoId: number = 0;
  private pedidoItemId: number = 0;
  private pedidoItemQuantidade: number = 0;

  constructor(
    private setorAPIService: SetorAPIService,
    private tarefaPedidoAPIService: TarefaPedidoAPIService,
    private produtoAPIService: ProdutoAPIService,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef
  ) {}

  ngAfterViewInit() {
    this.cdr.detectChanges();
  }

  /**
   * Abre o modal e carrega os setores disponíveis
   * @param produtoId ID do produto
   * @param nomeProduto Nome do produto para exibir no título
   * @param pedidoId ID do pedido
   * @param pedidoItemId ID do item do pedido
   * @param setoresExistentes Lista de setores já associados ao produto (opcional)
   */
  abrirModal(pedidoItem: PedidoItem, setoresExistentes?: ProdutoSetor[]) {
    this.produtoId = pedidoItem.produtoId;
    this.nomeProduto = pedidoItem.descricao;
    this.pedidoId = pedidoItem.pedidoId;
    this.pedidoItemId = pedidoItem.id;


    // Inicializa o objeto produto com o ID
    this.produto = {
      id: this.produtoId,
      descricao: this.nomeProduto
    } as Produto;

    // Normaliza os setores existentes para garantir a estrutura correta
    if (setoresExistentes && setoresExistentes.length > 0) {
      this.setoresSelecionados = setoresExistentes.map(s => ({
        id: s.id || 0,
        produtoId: s.produtoId || this.produtoId,
        setorId: s.setorId || (s as any).SetorId || s.setor?.id,
        setor: s.setor,
        ordenacao: s.ordenacao
      }));
      // Organiza os setores que já foram passados
      this.organizarSetoresComOrdem();
    } else {
      this.setoresSelecionados = [];
      this.setoresOrdenados = [];
    }

    // Carrega apenas a lista geral de setores disponíveis (única chamada HTTP)
    this.carregarSetores();

    setTimeout(() => {
      this.cdr.detectChanges();

      const modalElement = document.getElementById('modal-setores-producao');
      if (modalElement) {
        const existingInstance = bootstrap.Modal.getInstance(modalElement);
        if (existingInstance) {
          existingInstance.dispose();
        }

        this.modalInstance = new bootstrap.Modal(modalElement, {
          backdrop: 'static',
          keyboard: false
        });
        this.modalInstance.show();
      } else {
        console.error('Elemento do modal não encontrado: modal-setores-producao');
      }
    }, 100);
  }

  /**
   * Carrega a lista de setores disponíveis
   */
  private carregarSetores(): void {
    this.isLoading = true;
    this.setorAPIService.getLista('', null).subscribe({
      next: (response: any) => {
        this.setores = response || [];
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Erro ao carregar setores:', error);
        this.toastr.error('Erro ao carregar lista de setores', 'Erro');
        this.isLoading = false;
      }
    });
  }

  /**
   * Retorna os setores ordenados alfabeticamente por nome
   */
  get setoresOrdenadosAlfabeticamente(): Setor[] {
    return [...this.setores].sort((a, b) => {
      return a.nome.localeCompare(b.nome, 'pt-BR', { sensitivity: 'base' });
    });
  }

  /**
   * Retorna os setores da coluna esquerda (primeira metade)
   */
  get setoresColunaDireita(): Setor[] {
    const setoresOrdenados = this.setoresOrdenadosAlfabeticamente;
    const metade = Math.ceil(setoresOrdenados.length / 2);
    return setoresOrdenados.slice(0, metade);
  }

  /**
   * Retorna os setores da coluna direita (segunda metade)
   */
  get setoresColunaEsquerda(): Setor[] {
    const setoresOrdenados = this.setoresOrdenadosAlfabeticamente;
    const metade = Math.ceil(setoresOrdenados.length / 2);
    return setoresOrdenados.slice(metade);
  }

  /**
   * Carrega os setores vinculados ao produto
   */
  async carregarSetoresProduto(): Promise<void> {
    if (!this.produtoId) return;

    try {
      const setores = await this.produtoAPIService.getProdutoSetor(this.produtoId).toPromise() as ProdutoSetor[];
      this.setoresSelecionados = setores || [];
      this.organizarSetoresComOrdem();
    } catch (error) {
      console.error('Erro ao carregar setores do produto:', error);
    }
  }

  /**
   * Organiza os setores vinculados ao produto em uma estrutura ordenada
   */
  organizarSetoresComOrdem(): void {
    // Ordena os setores por ordenação
    const setoresOrdenados = this.setoresSelecionados.sort((a, b) => a.ordenacao - b.ordenacao);

    this.setoresOrdenados = setoresOrdenados.map(setor => ({
      id: setor.id,
      ordem: setor.ordenacao,
      setor: setor.setor,
      isEmAberto: false,
      produtoId: setor.produtoId,
      setorId: setor.setorId || setor.setor?.id
    }));
  }

  /**
   * Verifica se um setor está selecionado
   */
  isSetorSelecionado(setorId: number): boolean {
    return this.setoresOrdenados.some(item => item.setorId === setorId);
  }

  /**
   * Verifica se um setor deve estar desabilitado (já existe no produto)
   */
  isSetorDesabilitado(setorId: number): boolean {
    return this.setoresOrdenados.some(item => item.setorId === setorId);
  }

  /**
   * Adiciona um novo setor apenas se ainda não estiver selecionado
   */
  selectSetor(setor: Setor, event: Event) {
    const checkbox = event.target as HTMLInputElement;

    // Se o setor já existe (está desabilitado), não faz nada
    if (this.isSetorDesabilitado(setor.id)) {
      checkbox.checked = true; // Mantém marcado
      return;
    }

    // Se está marcando um novo setor
    if (checkbox.checked) {   
      this.adicionarNovoSetor(setor);
    }
  } 

  adicionarNovoSetor(setor: Setor): void {
    const proximaOrdem = this.setoresOrdenados.length + 1;

    const produtoSetor: ProdutoSetor = {
      produtoId: this.produto.id,
      setorId: setor.id,
      id: null,
      setor: setor,
      ordenacao: proximaOrdem
    };

    // Verifica se é produto novo
    if (this.produto.id === 0) {
      // Adiciona localmente sem chamar API
      this.setoresOrdenados.push({
        id: null,
        ordem: proximaOrdem,
        setor: setor,
        isEmAberto: false,
        produtoId: 0,
        setorId: setor.id
      });
      
      // Adiciona na lista de selecionados
      this.setoresSelecionados.push(produtoSetor);
      
      // Emite evento para o produto.component atualizar a lista
      this.setorAdicionadoLocal.emit(produtoSetor);
      
      this.toastr.success(`Setor "${setor.nome}" será vinculado ao salvar o produto`, 'Adicionado');
      return;
    }

    // Comportamento original para produtos existentes
    this.isTableLoading = true;
    this.loadingMessage = 'Adicionando setor...';
    
    this.produtoAPIService.createProdutoSetor(this.produto.id, produtoSetor).subscribe(async (res) => {
      this.toastr.success(`Setor "${setor.nome}" adicionado na ordem ${proximaOrdem}`, 'Sucesso');
      // Recarrega os setores do produto
      await this.carregarSetoresProduto();
      // Recria as tarefas com o novo setor
      //this.criarNovasTarefas();
      this.isTableLoading = false;
      this.loadingMessage = '';
    }, (error) => {
      this.toastr.error('Erro ao vincular Setor', 'Erro');
      this.isTableLoading = false;
      this.loadingMessage = '';
    });
  }

  /**
   * Remove um setor da lista de selecionados e atualiza as tarefas no banco
   */
  removerSetor(index: number): void {
    if (index < 0 || index >= this.setoresOrdenados.length) {
      this.toastr.error('Índice inválido', 'Erro');
      return;
    }

    const setorParaRemover = this.setoresOrdenados[index];

    // Se for produto novo, apenas remove localmente
    if (this.produto.id === 0) {
      const setorRemover = this.setoresSelecionados.find(x => x.setorId == setorParaRemover.setorId);

      this.setoresOrdenados.splice(index, 1);
      this.setoresSelecionados.splice(index, 1);
      
      // Recalcula ordenação
      this.setoresOrdenados.forEach((item, idx) => {
        item.ordem = idx + 1;
      });
      this.setoresSelecionados.forEach((item, idx) => {
        item.ordenacao = idx + 1;
      });
      
      

      this.setorRemovidoLocal.emit(setorRemover);
      this.toastr.success('Setor removido da lista', 'Sucesso');
      return;
    }

    // Comportamento original para produtos existentes
    if (!setorParaRemover.id) {
      this.toastr.error('Setor inválido', 'Erro');
      return;
    }

    this.isTableLoading = true;
    this.loadingMessage = 'Removendo setor...';
    
    // Remove o setor via API (reorganiza = true para ajustar a ordenação automaticamente)
    this.produtoAPIService.deleteProdutoSetor(this.produto.id, setorParaRemover.id, true)
      .subscribe(async (res) => {
        this.toastr.success('Setor removido com sucesso', 'Sucesso');
        // Recarrega os setores e recria as tarefas
        await this.carregarSetoresProduto();
        //this.criarNovasTarefas();
        this.isTableLoading = false;
        this.loadingMessage = '';
      }, (error) => {
        this.toastr.error('Erro ao deletar vínculo', 'Erro');
        this.isTableLoading = false;
        this.loadingMessage = '';
      });
  }

  /**
   * Recalcula a ordenação dos setores após mudanças
   */
  private recalcularOrdenacao(): void {
    this.setoresSelecionados.forEach((item, index) => {
      item.ordenacao = index + 1;
    });
  }

  /**
   * Move um setor para cima e atualiza no banco
   */
  upSetorOrder(setorVinculado: ProdutoSetor): void {
    if (setorVinculado.ordenacao <= 1) return;

    // Encontra o setor que está na posição acima
    const setorAcima = this.setoresOrdenados.find(s => s.ordem === setorVinculado.ordenacao - 1);
    
    if (setorAcima && setorAcima.isEmAberto) {
      this.toastr.warning('Não é possível mover para uma posição em aberto', 'Atenção');
      return;
    }

    const setorRequest = {
      id: setorVinculado.id,
      produtoId: setorVinculado.produtoId,
      setorId: setorVinculado.setor.id,
      ordenacao: setorVinculado.ordenacao - 1
    };

    this.updateSetor(setorRequest);
    
  }

  /**
   * Move um setor para baixo e atualiza no banco
   */
downSetorOrder(setorVinculado: ProdutoSetor): void {
    if (setorVinculado.ordenacao >= this.setoresOrdenados.length) return;

    // Encontra o setor que está na posição abaixo
    const setorAbaixo = this.setoresOrdenados.find(s => s.ordem === setorVinculado.ordenacao + 1);
    
    if (setorAbaixo && setorAbaixo.isEmAberto) {
      this.toastr.warning('Não é possível mover para uma posição em aberto', 'Atenção');
      return;
    }
  
    // Move apenas o setor atual para baixo
    const setorRequest = {
      id: setorVinculado.id,
      produtoId: setorVinculado.produtoId,
      setorId: setorVinculado.setor.id,
      ordenacao: setorVinculado.ordenacao + 1
    };

    this.updateSetor(setorRequest);
    
  }

  /**
   * Manipula o evento de drop do drag and drop
   */
  onDrop(event: CdkDragDrop<SetorComOrdem[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return; // Não mudou de posição
    }

    // Move o item no array local
    moveItemInArray(this.setoresOrdenados, event.previousIndex, event.currentIndex);

    // Atualiza a ordenação baseada na nova posição
    this.setoresOrdenados.forEach((item, index) => {
      item.ordem = index + 1;
    });

    // Atualiza também a lista de selecionados
    this.setoresSelecionados.forEach((item, index) => {
      const setorOrdenado = this.setoresOrdenados.find(s => s.setorId === item.setorId);
      if (setorOrdenado) {
        item.ordenacao = setorOrdenado.ordem;
      }
    });

    // Se for produto novo, apenas emite o evento para sincronizar com o componente pai
    if (this.produto.id === 0) {
      this.ordenacaoAlteradaLocal.emit([...this.setoresSelecionados]);
      this.toastr.success('Ordem atualizada', 'Sucesso');
      return;
    }

    // Busca o item que foi movido para atualizar no backend
    const itemMovido = this.setoresOrdenados[event.currentIndex];

    if (itemMovido && itemMovido.id) {
      const setorRequest = {
        id: itemMovido.id,
        produtoId: itemMovido.produtoId,
        setorId: itemMovido.setorId,
        ordenacao: itemMovido.ordem
      };

      this.updateSetor(setorRequest);
    }
  }

  /**
   * Atualiza os setores e recria as tarefas no banco de dados
   */
  updateSetor(setorRequest: any) {
    this.isTableLoading = true;
    this.loadingMessage = 'Atualizando ordem...';
    
    this.produtoAPIService.updateProdutoSetor(this.produto.id, setorRequest.id, setorRequest).subscribe(async (res) => {
      this.toastr.success('Ordem atualizada com sucesso', 'Sucesso');
      // Recarrega os setores para obter a nova ordenação
      await this.carregarSetoresProduto();
      // Recria as tarefas com a nova ordenação
      //this.criarNovasTarefas();
      this.isTableLoading = false;
      this.loadingMessage = '';
    }, (error) => {
      this.toastr.error('Erro ao atualizar ordem do setor', 'Erro');
      // Em caso de erro, recarrega os setores para restaurar o estado original
      this.carregarSetoresProduto();
      this.isTableLoading = false;
      this.loadingMessage = '';
    });
  }

  /**
   * Cria/recria as tarefas com a ordenação atual dos setores
   * Só executa se houver um pedido associado
   */
  private criarNovasTarefas(): void {
    // Só cria tarefas se estiver no contexto de pedidos
    if (!this.pedidoId || !this.pedidoItemId) {
      return;
    }

    const command = {
      PedidoId: this.pedidoId,
      PedidoItemId: this.pedidoItemId,
      ProdutoId: this.produtoId,
      Quantidade: this.pedidoItemQuantidade,
      Setores: this.setoresSelecionados.map(s => ({
        SetorId: s.setorId,
        Ordenacao: s.ordenacao
      }))
    };

    this.tarefaPedidoAPIService.criarNovasTarefas(command).subscribe({
      next: (response) => {

      },
      error: (error) => {
        console.error('[MODAL] Erro ao atualizar tarefas:', error);
        this.toastr.error('Erro ao atualizar tarefas', 'Erro');
      }
    });
  }
  
  convertToProdutoSetor(setorOrdem: SetorComOrdem): ProdutoSetor {
    return {
      id: setorOrdem.id,
      produtoId: setorOrdem.produtoId,
      setorId: setorOrdem.setorId!,
      setor: setorOrdem.setor!,
      ordenacao: setorOrdem.ordem
    };
  }

  /**
   * Salva os setores selecionados e cria as tarefas via API (se houver pedido)
   */
  salvar(): void {
    if (this.setoresSelecionados.length === 0) {
      this.toastr.warning('Selecione pelo menos um setor', 'Atenção');
      return;
    }

    // Se não houver pedido, apenas fecha o modal e emite o evento
    if (!this.pedidoId || !this.pedidoItemId) {
      this.setoresSalvos.emit([...this.setoresSelecionados]);
      this.fecharModal();
      return;
    }

    // Prepara o comando para criar as tarefas (contexto de pedidos)
    const command = {
      PedidoId: this.pedidoId,
      PedidoItemId: this.pedidoItemId,
      ProdutoId: this.produtoId,
      Setores: this.setoresSelecionados.map(s => ({
        SetorId: s.setorId,
        Ordenacao: s.ordenacao
      }))
    };

    this.setoresSalvos.emit([...this.setoresSelecionados]);
    this.fecharModal();
    // Chama o serviço para criar as tarefas
    /*this.isLoading = true;
    this.tarefaPedidoAPIService.criarNovasTarefas(command).subscribe({
      next: (response) => {
        this.isLoading = false;

        // Emite evento para o componente pai recarregar as tarefas
        this.setoresSalvos.emit([...this.setoresSelecionados]);
        this.fecharModal();
      },
      error: (error) => {
        console.error('[MODAL] Erro ao criar tarefas:', error);
        this.toastr.error('Erro ao criar tarefas de produção', 'Erro');
        this.isLoading = false;
      }
    });*/
  }

  /**
   * Fecha o modal
   */
  public fecharModal(): void {
    if (this.modalInstance) {
      this.modalInstance.hide();
    }
    this.setoresSelecionados = [];
    this.setores = [];
    this.nomeProduto = '';
    this.modalFechada.emit();
  }
}
